import { Component, forwardRef, Input, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonContent, IonItem, IonLabel, IonList, IonModal } from '@ionic/angular';

import { TypeAheadComponent, TypeAheadItemModel, UserAvatarComponent } from '@ninety/mobile/platform/components';

@Component({
  selector: 'nty-discussion-participant-select',
  imports: [IonContent, IonItem, IonLabel, IonList, IonModal, TypeAheadComponent, UserAvatarComponent],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DiscussionParticipantSelectComponent), multi: true },
  ],
  styles: [
    `
      @use 'terra';

      ion-list {
        background: transparent;
      }

      ion-item {
        color: terra.color(text);
      }

      ion-item::part(native) {
        --background: #{terra.color('background')};
        --border-color: #{terra.color(border)};
        --border-radius: #{terra.radius(xlarge)};
        --border-style: solid;
        --border-width: 1px;
      }

      ion-item.error::part(native) {
        --border-color: #{terra.color(red)};
      }

      .text {
        @include terra.text(body);
        color: terra.color(text);
      }

      .placeholder {
        --color: #{terra.color(text, lightest)};
        color: terra.color(text, lightest);
      }

      .icon {
        margin-inline-end: 4px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <ion-list>
      <ion-item
        button
        detail
        detail-icon="/assets/icons/caret-down--regular.svg"
        id="{{ ctrlId }}"
        lines="none"
        [class.error]="error">
        @if (selectedUser) {
          <div slot="start" class="icon">
            <nty-user-avatar
              size="24px"
              fontSize="10px"
              [userInitials]="selectedUser.initials || ''"
              [avatarUrl]="selectedUser.avatarUrl ?? ''" />
          </div>
        }
        <div>
          <ion-label class="terra-body text" [class.placeholder]="isPlaceholder">
            {{ ctrlLabel }}
          </ion-label>
        </div>
      </ion-item>
    </ion-list>

    <ion-modal trigger="{{ ctrlId }}" #addParticipantModal>
      <ng-template>
        <ion-content>
          <nty-type-ahead
            title="Select Participant"
            [items]="userOptions"
            [selectedItemId]="selectedUserId"
            (selectionChange)="onSelectionChange($event); addParticipantModal.dismiss()"
            (selectionCancel)="addParticipantModal.dismiss()" />
        </ion-content>
      </ng-template>
    </ion-modal>
  `,
})
export class DiscussionParticipantSelectComponent implements ControlValueAccessor {
  @Input({ required: true }) userOptions!: TypeAheadItemModel[];
  @Input() mode: 'manager' | 'managee' = 'manager';
  @Input() error: boolean | undefined = false;

  selectedUserId = '';
  selectedUser: TypeAheadItemModel | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChangeFn!: (val: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  onTouchFn!: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
  setDisabledState(isDisabled: boolean): void {}

  writeValue(managerId: string): void {
    this.setSelectedUser(managerId);
  }

  onSelectionChange(userId?: string): void {
    this.setSelectedUser(userId);
    this.onChangeFn?.(userId);
    this.onTouchFn?.();
  }

  get isPlaceholder() {
    return !this.selectedUser?.name;
  }

  get ctrlLabel() {
    if (this.selectedUser) {
      return this.selectedUser.name ?? '';
    }
    return 'Select participant';
  }

  get ctrlId() {
    return `add-${this.mode}-button`;
  }

  private setSelectedUser(userId?: string) {
    this.selectedUser = this.userOptions.find(u => u._id === userId) ?? null;
    this.selectedUserId = userId ?? '';
  }
}
